import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Check, AlertCircle, Save } from 'lucide-react';
import * as api from '../lib/esignApi';
import { useAuth } from '../hooks/useAuth';
import SignInRequired from '../components/SignInRequired';

const EXPIRATION_MIN = 1;
const EXPIRATION_MAX = 180;
const REMINDER_INTERVAL_MIN = 1;
const REMINDER_INTERVAL_MAX = 14;
const REMINDER_MAX_MIN = 0;
const REMINDER_MAX_MAX = 5;

export default function EsignSettings() {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [prefs, setPrefs] = useState(null);

  const load = useCallback(async () => {
    try {
      const { preferences } = await api.getEsignPreferences();
      setPrefs(preferences);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    load();
  }, [load, authLoading, user]);

  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(t);
  }, [saved]);

  const handleChange = (key, value) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = useCallback(async () => {
    if (!prefs) return;
    setSaving(true);
    setError(null);
    try {
      const { preferences } = await api.updateEsignPreferences({
        default_expiration_days: Number(prefs.default_expiration_days),
        default_reminder_interval_days: Number(prefs.default_reminder_interval_days),
        default_reminder_max_count: Number(prefs.default_reminder_max_count),
        notify_on_each_signer: !!prefs.notify_on_each_signer,
        daily_summary: !!prefs.daily_summary,
        sender_display_name: prefs.sender_display_name?.trim() || null,
        preferred_timezone: prefs.preferred_timezone?.trim() || null,
      });
      setPrefs(preferences);
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [prefs]);

  // Resolve the browser's detected timezone once for the placeholder hint.
  const detectedTz = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  })();

  // Logged-out (e.g. a licensed desktop user who signed out): eSign needs a
  // live session — prompt sign-in instead of a dead page.
  if (!authLoading && !user) {
    return <SignInRequired feature="eSign" />;
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
      </div>
    );
  }

  if (!prefs) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Unable to load preferences.</p>
        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-2xl mx-auto">
      <Link
        to="/esign"
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition mb-5"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to envelopes
      </Link>

      <h1 className="text-2xl font-bold text-gray-900 mb-2">eSign settings</h1>
      <p className="text-sm text-gray-500 mb-6">
        Defaults that apply to every envelope you send. You can still override them per-envelope
        during the send flow.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Expiration */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-5">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
          Expiration
        </h2>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Default expiration</span>
          <div className="flex items-center gap-2 mt-1">
            <input
              type="number"
              min={EXPIRATION_MIN}
              max={EXPIRATION_MAX}
              value={prefs.default_expiration_days}
              onChange={(e) => handleChange('default_expiration_days', e.target.value)}
              className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-600">days after send</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Between {EXPIRATION_MIN} and {EXPIRATION_MAX}. Signers get a last-chance email 72 and 24 hours before expiry.
          </p>
        </label>
      </section>

      {/* Reminders */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-5">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
          Reminders
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Send a reminder every</span>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number"
                min={REMINDER_INTERVAL_MIN}
                max={REMINDER_INTERVAL_MAX}
                value={prefs.default_reminder_interval_days}
                onChange={(e) => handleChange('default_reminder_interval_days', e.target.value)}
                className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-600">days</span>
            </div>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Up to this many reminders</span>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number"
                min={REMINDER_MAX_MIN}
                max={REMINDER_MAX_MAX}
                value={prefs.default_reminder_max_count}
                onChange={(e) => handleChange('default_reminder_max_count', e.target.value)}
                className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-600">per recipient</span>
            </div>
          </label>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Set reminders to 0 to disable automatic reminders. You can still resend invites manually
          at any time.
        </p>
      </section>

      {/* Notifications */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-5">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
          Email notifications
        </h2>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!prefs.notify_on_each_signer}
            onChange={(e) => handleChange('notify_on_each_signer', e.target.checked)}
            className="mt-1 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-900">Email me when each signer completes</span>
            <p className="text-xs text-gray-500 mt-0.5">
              Get notified as signatures trickle in. The completion email still fires when everyone's done.
            </p>
          </div>
        </label>

        <label className="flex items-start gap-3 cursor-pointer mt-4">
          <input
            type="checkbox"
            checked={!!prefs.daily_summary}
            onChange={(e) => handleChange('daily_summary', e.target.checked)}
            className="mt-1 w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
          />
          <div>
            <span className="text-sm font-medium text-gray-900">Daily summary email</span>
            <p className="text-xs text-gray-500 mt-0.5">
              A one-a-day 8am digest covering yesterday's sends + completions, envelopes stalled 7+
              days, and anything expiring in the next 72 hours. Empty days are skipped.
            </p>
          </div>
        </label>

        {prefs.daily_summary ? (
          <div className="mt-4 ml-7">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">Digest timezone</span>
              <input
                type="text"
                value={prefs.preferred_timezone || ''}
                onChange={(e) => handleChange('preferred_timezone', e.target.value)}
                placeholder={`${detectedTz} (detected). Leave blank for UTC.`}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                IANA timezone name (e.g. <code>America/Los_Angeles</code>). Digest fires at 8am in
                this timezone.
              </p>
            </label>
            <button
              type="button"
              onClick={() => handleChange('preferred_timezone', detectedTz)}
              className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 hover:underline"
            >
              Use my browser's timezone ({detectedTz})
            </button>
          </div>
        ) : null}
      </section>

      {/* Sender identity */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
          Sender identity
        </h2>
        <label className="block">
          <span className="text-sm font-medium text-gray-700">Display name override</span>
          <input
            type="text"
            maxLength={128}
            placeholder="Defaults to your account name"
            value={prefs.sender_display_name || ''}
            onChange={(e) => handleChange('sender_display_name', e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <p className="text-xs text-gray-400 mt-1">
            Appears in the "from" line of signer invites and the public verification page.
          </p>
        </label>
      </section>

      <div className="flex items-center justify-end gap-3">
        {saved && (
          <span className="text-sm text-green-600 flex items-center gap-1">
            <Check className="w-4 h-4" />
            Saved
          </span>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 text-sm font-medium disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving…
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save changes
            </>
          )}
        </button>
      </div>
    </div>
  );
}
